import {
  Box,
  CircularProgress,
  Chip,
  Pagination,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import ViewWeekOutlinedIcon from "@mui/icons-material/ViewWeekOutlined";
import { useMemo } from "react";
import { OssStatusChip } from "../OssStatusChip";
import { riskLevel } from "../../constants/labels";
import { OssOrderMobileCard } from "./OssOrderMobileCard";
import { OssOrderToolbar } from "./OssOrderToolbar";
import {
  buildOrderRowSummary,
  getOrderItemSummaryText,
  getOrderQuantitySummaryText,
} from "../../utils/orderStatusSummaryUtils";

const columns = ["주문번호", "품목 상태", "거래처", "품목", "수량", "요청 납기", "착수 필요일", "일정 상태", "위험도", "여유일"];

const OrderStatusSummary = ({ summary }) => {
  const entries = summary?.entries || [];
  return (
    <Stack direction="row" spacing={0.45} justifyContent="center" alignItems="center" flexWrap="wrap" useFlexGap>
      <Chip
        size="small"
        label={`품목 ${summary?.lineCount || 1}`}
        variant="outlined"
        sx={{
          height: 22,
          borderRadius: "999px",
          borderColor: "#cbd5e1",
          bgcolor: "#f8fafc",
          color: "#475569",
          fontSize: 11.5,
          fontWeight: 800,
        }}
      />
      {entries.length ? (
        entries.slice(0, 3).map((entry) => (
          <Chip
            key={entry.key}
            size="small"
            label={entry.label}
            variant="outlined"
            sx={{
              height: 22,
              borderRadius: "999px",
              borderColor: `${entry.tone}55`,
              bgcolor: entry.bg,
              color: entry.tone,
              fontSize: 11.5,
              fontWeight: 800,
            }}
          />
        ))
      ) : (
        <Chip
          size="small"
          label="모두 정상"
          variant="outlined"
          sx={{
            height: 22,
            borderRadius: "999px",
            borderColor: "#14b8a655",
            bgcolor: "#ecfeff",
            color: "#0f766e",
            fontSize: 11.5,
            fontWeight: 800,
          }}
        />
      )}
    </Stack>
  );
};

export const OssOrderTable = ({ compact, filters, loading, viewData, selectedOrderId, onFilterChange, onOpen }) => {
  const rowSummaries = useMemo(
    () => new Map(viewData.items.map((row) => [String(row.order_id || row.order_no || row.order_line_id), buildOrderRowSummary(row)])),
    [viewData.items]
  );
  const getSummary = (row) => rowSummaries.get(String(row.order_id || row.order_no || row.order_line_id));

  return (
    <>
      <OssOrderToolbar filters={filters} total={viewData.pagination.total} onFilterChange={onFilterChange} />

    {loading ? (
      <Box sx={{ minHeight: 420, display: "grid", placeItems: "center" }}>
        <CircularProgress size={32} />
      </Box>
    ) : compact ? (
      <Stack spacing={1} sx={{ p: 1.25 }}>
        {viewData.items.length === 0 ? (
          <Box sx={{ minHeight: 280, display: "grid", placeItems: "center", color: "#6b7280" }}>
            조건에 맞는 주문 일정이 없습니다.
          </Box>
        ) : (
          viewData.items.map((row) => (
            <OssOrderMobileCard
              key={row.order_line_id}
              row={row}
              summary={getSummary(row)}
              onOpen={() => onOpen(row.order_id || row.order_line_id)}
            />
          ))
        )}
      </Stack>
    ) : (
      <TableContainer sx={{ minHeight: 520, maxHeight: "calc(100vh - 356px)", bgcolor: "#fff" }}>
        <Table stickyHeader size="small" sx={{ minWidth: 1320 }}>
          <TableHead>
            <TableRow>
              {columns.map((label) => (
                <TableCell
                  key={label}
                  align={label === "수량" || label === "여유일" ? "right" : "center"}
                  sx={{
                    height: 44,
                    fontSize: 12.5,
                    fontWeight: 800,
                    color: "#374151",
                    bgcolor: "#f3f6fa",
                    borderColor: "#e1e6ef",
                  }}
                >
                  {label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {viewData.items.length === 0 ? (
              <TableRow>
                <TableCell colSpan={10} align="center" sx={{ height: 320, color: "#6b7280" }}>
                  조건에 맞는 주문 일정이 없습니다.
                </TableCell>
              </TableRow>
            ) : (
              viewData.items.map((row) => {
                const riskMeta = riskLevel[row.risk_level] || riskLevel.NORMAL;
                const selected = String(row.order_id || row.order_line_id) === String(selectedOrderId);
                return (
                  <TableRow
                    key={row.order_line_id}
                    hover
                    onClick={() => onOpen(row.order_id || row.order_line_id)}
                    sx={{
                      "& td": { height: 58, borderColor: "#e8edf4", fontSize: 13 },
                      boxShadow: `inset 4px 0 0 ${riskMeta.tone}`,
                      bgcolor: selected ? "#f5f3ff" : "#fff",
                      cursor: "pointer",
                      "&:hover": { bgcolor: selected ? "#ede9fe" : "#fbfbfe" },
                    }}
                  >
                    <TableCell align="center">
                      <Typography sx={{ fontSize: 13.5, fontWeight: 800 }}>{row.order_no}</Typography>
                      <Typography sx={{ fontSize: 11.5, color: "#6b7280" }}>
                        {row.customer_po_no || `품목 ${row.line_no}`}
                      </Typography>
                    </TableCell>
                    <TableCell align="center" sx={{ minWidth: 180 }}>
                      <OrderStatusSummary summary={getSummary(row)} />
                    </TableCell>
                    <TableCell align="center">
                      <Typography sx={{ fontSize: 13 }}>{row.customer_name}</Typography>
                      <Typography sx={{ fontSize: 11.5, color: "#6b7280" }}>{row.site_name}</Typography>
                    </TableCell>
                    <TableCell sx={{ maxWidth: 310 }}>
                      <Typography noWrap sx={{ fontSize: 13.5, fontWeight: 700 }}>
                        {getOrderItemSummaryText(row)}
                      </Typography>
                      <Typography noWrap sx={{ fontSize: 11.5, color: "#6b7280" }}>
                        {row.item_code_snapshot} · {row.specification_snapshot || "규격 미등록"}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      {getOrderQuantitySummaryText(row)}
                    </TableCell>
                    <TableCell align="center">{row.requested_due_date}</TableCell>
                    <TableCell align="center">{row.required_start_date || "-"}</TableCell>
                    <TableCell align="center"><OssStatusChip value={row.schedule_status} /></TableCell>
                    <TableCell align="center"><OssStatusChip type="risk" value={row.risk_level} /></TableCell>
                    <TableCell align="right">{row.available_margin_days ?? "-"}</TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>
    )}

    <Box
      sx={{
        py: 1.3,
        px: 2,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        borderTop: "1px solid #e6eaf0",
        bgcolor: "#fbfbfe",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, color: "#6b7280" }}>
        <ViewWeekOutlinedIcon fontSize="small" />
        <Typography sx={{ fontSize: 12.5 }}>주문번호 단위 표시</Typography>
      </Box>
      <Pagination
        page={viewData.pagination.page || 1}
        count={viewData.pagination.totalPages || 1}
        onChange={(_, page) => onFilterChange("page", page)}
        color="primary"
        size="small"
      />
    </Box>
  </>
  );
};
